import { $http } from "@/plugins/api";
import { getApiClientUrl } from "@/config/runtimeConfig";
import type { Commit } from "vuex";
import {
  Apiservice,
  Organisation,
  Uuid,
  OrganisationsTotal,
  OrganisationConnection,
} from "../../types/organisatio";
import { Params } from "@/types/api";
import { decodeOrgFromToken } from "@/utils/orgToken";

type OrgWithAncestors = Organisation & {
  parent_uuid?: string | null;
  ancestors?: OrgWithAncestors[];
};

const normalizeOrgName = (org: OrgWithAncestors) => {
  const isRootSystem = org.organisation_type === "System" && !org.parent_uuid;
  return isRootSystem ? { ...org, name: "System" } : org;
};

interface State {
  organisations: Organisation[];
  organisationsConnections: OrganisationConnection[];
  organisationsTotal: OrganisationsTotal;
  orgsPerCustomer: Organisation[];
  orgByUuid: Record<string, OrgWithAncestors>;
}

const state: State = {
  organisations: [],
  organisationsConnections: [],
  organisationsTotal: { total: 0 },
  orgsPerCustomer: [],
  orgByUuid: {},
};

// Track in-flight org fetches to avoid duplicate calls per UUID
const inFlightOrgFetches: Record<
  string,
  Promise<OrgWithAncestors | null> | undefined
> = {};
const inFlightOrgLists: Record<string, Promise<unknown> | undefined> = {};

const getters = {
  orginisationsList: (state: { organisations: State }) => state.organisations,
  organisationsConnections: (state: { organisationsConnections: State }) =>
    state.organisationsConnections,
  organisationsTotal: (state: { organisationsTotal: State }) =>
    state.organisationsTotal,
  orgsPerCustomer: (state: { orgsPerCustomer: State }) => state.orgsPerCustomer,
  orgByUuid: (state: State) => (uuid: string) => state.orgByUuid?.[uuid],
};

const actions = {
  clearOrgCache({ commit }: { commit: Commit }) {
    commit("resetState", []);
  },

  async getOrgWithAncestors(
    {
      commit,
      getters,
      rootGetters,
    }: {
      commit: Commit;
      getters: Record<string, unknown>;
      rootGetters: Record<string, unknown>;
    },
    { uuid }: { uuid: string },
  ) {
    const inflight = inFlightOrgFetches[uuid];
    if (inflight) return inflight;

    const getByUuid = getters.orgByUuid as
      | ((id: string) => OrgWithAncestors | undefined)
      | undefined;
    const cached = getByUuid ? getByUuid(uuid) : undefined;
    if (cached?.ancestors && cached?.name) {
      return cached;
    }

    const fetchPromise = (async () => {
      // Walk parents until root using /v1/idp/orgs (no include=ancestors until API is ready)
      const ancestors: OrgWithAncestors[] = [];
      let org: OrgWithAncestors | null = null;
      let childOrg: OrgWithAncestors | null = null;
      let currentUuid: string | null = uuid;
      const safety = 20;
      const token =
        (rootGetters["authentication/token"] as
          | { access_token?: string }
          | string
          | undefined) || null;
      const access =
        typeof token === "string" ? token : token?.access_token || null;
      const selectedAccountUuid = decodeOrgFromToken(access)?.uuid || null;
      let hops = 0;
      while (currentUuid && hops < safety) {
        const cachedParent = getByUuid ? getByUuid(currentUuid) : undefined;
        const res = cachedParent
          ? { data: cachedParent }
          : await $http.get(`${getApiClientUrl()}/v1/idp/orgs/${currentUuid}`);
        const current = normalizeOrgName(res.data as OrgWithAncestors);
        if (!childOrg) {
          childOrg = current; // first iteration is the requested org
        } else {
          ancestors.unshift({
            uuid: current.uuid,
            name: current.name,
            organisation_type: current.organisation_type,
            parent_uuid: current.parent_uuid,
          } as OrgWithAncestors);
        }
        currentUuid = current.parent_uuid || null;
        const hitSignedIn =
          selectedAccountUuid && current.uuid === selectedAccountUuid;
        const hitRoot = !current.parent_uuid;
        if (hitSignedIn || hitRoot) {
          org = { ...childOrg, ancestors } as OrgWithAncestors;
          break;
        }
        hops += 1;
      }

      if (org) {
        commit("setOrgDetail", org);
      }
      return org;
    })();

    inFlightOrgFetches[uuid] = fetchPromise.finally(() => {
      delete inFlightOrgFetches[uuid];
    });

    return fetchPromise;
  },

  async updateOrg(
    { commit }: { commit: Commit },
    { formData, parent_uuid, organisation_type }: Apiservice,
  ) {
    const postData = {
      ...(formData.name && { name: formData.name }),
      ...(formData.country && { country: formData.country }),
      ...(parent_uuid && { parent_uuid: parent_uuid }),
      ...(formData.description && { description: formData.description }),
      ...(formData.customerID && {
        billing_system_id: Number(formData.customerID),
      }),
      ...(organisation_type && {
        organisation_type: organisation_type,
      }),
    };
    const res = await $http.patch(
      `${getApiClientUrl()}/v1/idp/orgs/${formData.uuid}`,
      postData,
    );
    commit("updateOrg", res.data);
    return res.data;
  },

  async showOrg({ commit }: { commit: Commit }, { uuid }: Uuid) {
    if (!uuid || uuid === undefined) {
      return null;
    }
    const res = await $http.get(
      `${getApiClientUrl()}/v1/idp/orgs/${uuid}?include=licences`,
    );
    commit("updateOrg", res.data);
    return res;
  },

  async getCustomerPerUsageOrg(
    { commit }: { commit: Commit },
    {
      parent_uuid,
      solution_uuid,
      limit,
      filter,
      sortdir,
      offset = 0,
      sortkey = null,
    }: {
      parent_uuid?: string;
      solution_uuid?: string;
      limit?: number;
      filter?: string;
      sortdir?: string;
      offset?: number;
      sortkey?: string | null;
    },
  ) {
    let url = `${getApiClientUrl()}/v1/idp/orgs?include=licences`;

    if (parent_uuid) url += `&parent_uuid=${parent_uuid}`;
    if (solution_uuid) url += `&solution_uuid=${solution_uuid}`;
    if (limit) url += `&limit=${limit}`;
    if (filter) url += `&filter=${filter}`;
    if (sortkey) url += `&sortkey=${sortkey}`;
    if (offset) url += `&offset=${offset}`;
    if (sortdir) url += `&sortdir=${sortdir}`;

    const orgsResponse = await $http.get(url);

    commit("orgsPerCustomerList", orgsResponse.data.data);
  },

  async getOrginisationsList(
    { commit }: { commit: Commit },
    {
      parent_uuid,
      solution_uuid,
      params,
      with_connections,
    }: {
      parent_uuid: string | null;
      solution_uuid: string | null;
      params: Params;
      with_connections: boolean;
    },
  ) {
    const key = JSON.stringify({
      parent_uuid,
      solution_uuid,
      params,
      with_connections,
    });
    if (inFlightOrgLists[key]) {
      return inFlightOrgLists[key];
    }

    const fetchPromise = (async () => {
      const baseUrl = `${getApiClientUrl()}/v1/idp/orgs`;
      // VITE_API_CLIENT can be relative (e.g. "/api") on environments that
      // serve SPA + API from the same origin via a proxy. The second arg
      // resolves relative paths against the page origin; absolute baseUrls
      // ignore the base entirely, so it's a no-op there.
      const url = new URL(baseUrl, globalThis.location.origin);
      const reqParams = new URLSearchParams();
      reqParams.append("include", "licences");
      if (parent_uuid) {
        reqParams.append("parent_uuid", parent_uuid);
      }
      if (solution_uuid) {
        reqParams.append("solution_uuid", solution_uuid);
      }

      // Convert params to lowercase strings and append them to reqParams
      for (const [key, value] of Object.entries(params)) {
        reqParams.append(key, (value ?? "").toString().toLowerCase());
      }

      url.search = reqParams.toString();

      const orgsResponse = await $http.get(url.toString());
      const uuids = orgsResponse.data.data
        .map((obj: { uuid: string }) => obj.uuid)
        .join(",");

      if (with_connections && uuids) {
        const url = `${getApiClientUrl()}/v1/pt/organisations/connection-status?uuids=${uuids}`;
        const test = await $http.get(url);
        commit("orgsConnections", test.data.results);
      }

      commit("orgsList", orgsResponse.data.data);
      commit("orgsTotal", orgsResponse.data.total);
      return orgsResponse.data;
    })();

    inFlightOrgLists[key] = fetchPromise.finally(() => {
      delete inFlightOrgLists[key];
    });

    return fetchPromise;
  },

  async getOrganisationsConnections(
    { commit }: { commit: Commit },
    {
      uuids,
    }: {
      uuids: string;
    },
  ) {
    const url = `${getApiClientUrl()}/v1/pt/organisations/connection-status?uuids=${uuids}`;
    const orgsResponse = await $http.get(url);
    commit("orgsConnections", orgsResponse.data.results);
  },

  // This is a new IDP organisations "batch" delete endpoint:
  async batchDeleteOrganisations(
    { commit }: { commit: Commit },
    { accounts }: { accounts: { uuid: string }[] },
  ) {
    const deletedUuidsResponse = [];

    for (const account of accounts) {
      const response = await $http.delete(
        `${getApiClientUrl()}/v1/idp/orgs/${account.uuid}`,
        {
          _hasCustomErrorHandling: true,
        },
      );
      if (response.data && response.data.uuid) {
        deletedUuidsResponse.push(response.data.uuid);
      }
    }

    commit("deleteOrgs", deletedUuidsResponse);

    return deletedUuidsResponse;
  },

  async createPhishTitanOrg(_context: unknown, organisation_uuid: unknown) {
    await $http.post(`${getApiClientUrl()}/v1/pt/orgs`, {
      organisationID: organisation_uuid,
      globalOrganisationID: organisation_uuid,
    });
  },

  async createOrginisation(
    { commit }: { commit: Commit },
    { formData, parent_uuid, organisation_type }: Apiservice,
  ) {
    const url = parent_uuid
      ? `${getApiClientUrl()}/v1/idp/orgs?parent_uuid=${parent_uuid}`
      : `${getApiClientUrl()}/v1/idp/orgs`;

    const postData: { [key: string]: unknown } = {
      ...(formData.name && { name: formData.name }),
      ...(formData.country && { country: formData.country }),
      ...(formData.description && { description: formData.description }),
      ...(formData.customerID && {
        billing_system_id: Number(formData.customerID),
      }),
      ...(organisation_type && {
        organisation_type: organisation_type,
      }),
    };
    postData.parent_uuid = parent_uuid;
    const res = await $http.post(url, postData);
    commit("updateOrgsList", res.data);
    return res;
  },
};

const mutations = {
  setOrgDetail: (state: State, org: OrgWithAncestors) => {
    const normalizedOrg = normalizeOrgName(org);
    state.orgByUuid = {
      ...state.orgByUuid,
      [org.uuid]: normalizedOrg,
    };
  },
  resetState(state: State, data: Organisation[]): void {
    state.organisations = [...data];
    state.organisationsConnections = [];
    state.organisationsTotal = { total: 0 };
    state.orgsPerCustomer = [];
    state.orgByUuid = {};
  },
  deleteOrgs(state: { organisations: Organisation[] }, ids: string[]) {
    for (const id of ids) {
      state.organisations = state.organisations.filter(
        (organisation) => organisation.uuid !== id,
      );
    }
  },
  orgsPerCustomerList: (
    state: { orgsPerCustomer: Organisation[] },
    data: Organisation[],
  ) => (state.orgsPerCustomer = data),
  orgsList: (state: { organisations: Organisation }, orgs: Organisation) =>
    (state.organisations = orgs),
  orgsConnections: (
    state: { organisationsConnections: OrganisationConnection[] },
    organisationsConnections: OrganisationConnection[],
  ) => (state.organisationsConnections = organisationsConnections || []),
  orgsTotal: (
    state: { organisationsTotal: OrganisationsTotal },
    total: number,
  ) => (state.organisationsTotal = { total: total }),
  updateOrgsList(state: { organisations: Organisation[] }, org: Organisation) {
    state.organisations.push(normalizeOrgName(org));
  },
  updateOrg(
    state: { organisations: Organisation[] },
    updatedOrg: Organisation,
  ): void {
    state.organisations = state.organisations.map((org) =>
      org.uuid === updatedOrg.uuid ? normalizeOrgName(updatedOrg) : org,
    );
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
